import { useEffect, useState } from "react";
import { GuidePostService } from "@oldDashboard/legacyServices/guide-posts/guidePostService";
import { formatDateCharts } from "@oldDashboard/legacyUtils";
import { Coordinates } from "@oldDashboard/legacyModels/coordinates.model";
import { ListSizeOverTime } from "@oldDashboard/legacyModels/listSizeOverTime.model";
import { ChartName } from "@oldDashboard/legacyModels/chart.model";
import { setListSizeOverTimeAction } from "@oldDashboard/legacyActions/dashboardClient";

interface useListOverTimeProps { affiliateSelected: String, launchSelected: String, timePeriodSelected: String, searching: boolean }
const useListSizeOverTime = (props:useListOverTimeProps) => {
    const [chartData, setChartData] = useState<ChartName>();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        fetchListSizeOverTime();
    }, [props.searching, props.launchSelected]);

    const buildChartData = (data: ListSizeOverTime[]) : ChartName =>{
        // Extract all y values from the data
        const yValues = data.flatMap(({ data }) => data.map((x: Coordinates) => Number(x.y)));
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);
        let step;
        if (maxY <= 2000) {
            step = 200;
        } else if (maxY <= 5000) {
            step = 1000;
        } else if (maxY >= 10000) {
            step = 5000;
        } else {
            step = 1000;
        }
        const padding = Math.max(step, Math.ceil((maxY - minY) * 0.05));
        let dynamicMin = Math.floor((minY - padding) / step) * step;
        if (dynamicMin < 0) dynamicMin = 0;
        const dynamicMax = Math.ceil((maxY + padding) / step) * step;
        const tickAmount = Math.max(1, Math.round((dynamicMax - dynamicMin) / step));
        
        return  {
            series: [data.map(({name, data} : {name: string, data:Coordinates[]}) => {
                return {
                    name:name,
                    data: data.map(x=>Number(x.y))
            } })[0]],
            options: {
                chart: {
                    height: 320,
                    type: 'line',
                    dropShadow: {
                        enabled: true,
                        color: '#000',
                        top: 18,
                        left: 7,
                        blur: 10,
                        opacity: 0.2
                    },
                    toolbar: {
                        show: false
                    },
                    events: {
                        mounted: (chart:any) => {
                            chart.windowResizeHandler();
                        }
                    },
                },
                colors: ['#845adf', '#23b7e5'],
                dataLabels: {
                    enabled: true,
                },
                stroke: {
                    curve: 'smooth'
                },

                grid: {
                    borderColor: '#f2f5f7',
                },
                markers: {
                    size: 1
                },
                xaxis: {
                    categories: data.map(({data} : {data : Coordinates[]}) => {return data.map(x=>x.x)})[0],
                    labels: {
                        show: true,
                        style: {
                            colors: "#8c9097",
                            fontSize: '11px',
                            fontWeight: 600,
                            cssClass: 'apexcharts-xaxis-label',
                        },
                        formatter: function(value: string) {
                            if(value){
                                return formatDateCharts(value);
                            }
                     }
                    },
                },
                yaxis: {
                    min: dynamicMin,
                    max: dynamicMax,
                    tickAmount: tickAmount,
                    labels: {
                        show: true,
                        style: {
                            colors: "#8c9097",
                            fontSize: '11px',
                            fontWeight: 600,
                            cssClass: 'apexcharts-yaxis-label',
                        },
                        formatter: function(value: number) {
                            if (value === 0) return '0';
                            if (value >= 1000) return (value / 1000) + 'K';
                            return value;
                        }
                    }
                },
                legend: {
                    customLegendItems: data.map(({data} : {data : Coordinates[]}) => {return data.map(x=>{return {added: x.a, removed: x.r}})})[0],
                    position: 'top',
                    horizontalAlign: 'right',
                    floating: true,
                    offsetY: -25,
                    offsetX: -5
                },
                tooltip: {
                    theme: 'dark',
                    custom: function(items: any) {
                        return '<div class="arrow_box px-2 py-2">' +
                          '<span>'+ 'Added: '+ items.w.config.legend.customLegendItems[items.dataPointIndex].added + '</span>' +
                          '<br>'+
                          '<span>'+ 'Removed: '+ items.w.config.legend.customLegendItems[items.dataPointIndex].removed + '</span>' +
                          '</div>'
                      }
                }
            }

        };        
    }

    const fetchListSizeOverTime = async() => {
        setIsLoading(true);
        try {
            const service = new GuidePostService();
            const response = await service.getTopListSizeOverTime(props.launchSelected, props.timePeriodSelected);
            if (response) {
                setListSizeOverTimeAction(response);
                const data = buildChartData(response);
                setChartData(data);
            }
        } catch (error: any) {
            console.log("ERROR ", error)
        } finally {
            setIsLoading(false);
        }
    }

    return { isLoading, chartData, fetchListSizeOverTime }
}

export default useListSizeOverTime;